import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { SEOHead } from "@/components/shared/SEOHead";

export const Route = createFileRoute("/business/orders")({
  component: BusinessOrdersPage,
});

function BusinessOrdersPage() {
  const [email, setEmail] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [lookupDone, setLookupDone] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const errors: Record<string, string> = {};
    if (!email.trim()) errors["email"] = "Email is required";
    if (!orderNumber.trim()) errors["orderNumber"] = "Order number is required";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLookupLoading(true);
    setFieldErrors({});

    // In production, this would call the orders API
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setLookupDone(true);
    setLookupLoading(false);
  };

  return (
    <div className="page-enter bp-utility-page bp-business-page">
      <SEOHead
        title="Track Your Orders"
        description="Look up the status of your Quuil business orders."
        path="/business/orders"
        robots="noindex,follow"
      />

      {/* Header */}
      <section className="bg-qds-background border-b border-qds-border" aria-labelledby="orders-heading">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-qds-text-tertiary mb-4">
            <Link to="/business" className="hover:text-qds-text-secondary transition-colors min-h-[44px] inline-flex items-center">
              Business
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-qds-text-primary" aria-current="page">Orders</span>
          </nav>
          <h1 id="orders-heading" className="text-3xl md:text-4xl font-light tracking-tight text-qds-text-primary mb-2">
            Track Your Orders
          </h1>
          <p className="text-qds-text-secondary text-sm">
            Check the status of any business order
          </p>
        </div>
      </section>

      {/* Order Lookup */}
      <div className="max-w-xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <div className="bg-qds-surface border border-qds-border rounded-xl p-6 md:p-8">
          <h2 className="text-lg font-medium text-qds-text-primary mb-5">
            Find Your Order
          </h2>

          <form onSubmit={handleLookup} noValidate className="space-y-4">
            <div>
              <label htmlFor="order-email" className="block text-sm font-medium text-qds-text-primary mb-1.5">
                Email Address <span aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                id="order-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors((prev) => { const n = { ...prev }; delete n["email"]; return n; }); }}
                required
                autoComplete="email"
                aria-invalid={fieldErrors["email"] ? true : undefined}
                aria-describedby={fieldErrors["email"] ? "order-email-error" : undefined}
                className={cn(
                  "w-full px-4 py-2.5 bg-qds-surface-elevated border rounded-lg text-sm focus:outline-none transition-colors min-h-[44px]",
                  fieldErrors["email"]
                    ? "border-qds-error"
                    : "border-qds-border focus:border-qds-border-strong"
                )}
                placeholder="you@company.com"
              />
              {fieldErrors["email"] && (
                <p id="order-email-error" className="field-error" role="alert">{fieldErrors["email"]}</p>
              )}
            </div>

            <div>
              <label htmlFor="order-number" className="block text-sm font-medium text-qds-text-primary mb-1.5">
                Order Number <span aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                id="order-number"
                type="text"
                value={orderNumber}
                onChange={(e) => { setOrderNumber(e.target.value); setFieldErrors((prev) => { const n = { ...prev }; delete n["orderNumber"]; return n; }); }}
                required
                aria-invalid={fieldErrors["orderNumber"] ? true : undefined}
                aria-describedby={fieldErrors["orderNumber"] ? "order-number-error" : undefined}
                className={cn(
                  "w-full px-4 py-2.5 bg-qds-surface-elevated border rounded-lg text-sm focus:outline-none transition-colors min-h-[44px]",
                  fieldErrors["orderNumber"]
                    ? "border-qds-error"
                    : "border-qds-border focus:border-qds-border-strong"
                )}
                placeholder="e.g., QUL-12345"
              />
              {fieldErrors["orderNumber"] && (
                <p id="order-number-error" className="field-error" role="alert">{fieldErrors["orderNumber"]}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={lookupLoading}
              className="w-full bg-qds-primary text-qds-text-inverse px-6 py-3 rounded-xl font-medium text-sm hover:bg-qds-primary-pressed transition-colors disabled:opacity-60 min-h-[44px]"
            >
              {lookupLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
                  Looking up...
                </span>
              ) : (
                "Find order"
              )}
            </button>
          </form>

          {lookupDone && (
            <div className="mt-6 p-4 bg-qds-surface-elevated border border-qds-border rounded-lg text-center" role="status">
              <p className="text-sm text-qds-text-secondary">
                We couldn't find an order matching those details.
                Double-check and try again, or reach out to{" "}
                <a href="mailto:business@quuil.com" className="underline text-qds-text-primary hover:text-qds-text-secondary transition-colors">
                  business@quuil.com
                </a>{" "}
                and we'll help you find it.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-qds-text-tertiary mb-3">
            Questions about an order?
          </p>
          <a
            href="mailto:business@quuil.com"
            className="inline-flex items-center gap-2 text-sm font-medium text-qds-text-primary hover:underline min-h-[44px]"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            business@quuil.com
          </a>
        </div>
      </div>
    </div>
  );
}
